'use client';

import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import logReviewGPTFeedbackClicked from '@alltrails/amplitude/events/logReviewGPTFeedbackClicked';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import getScreenReaderRatingText from '@alltrails/core/utils/getScreenReaderRatingText';
import { fullName } from '@alltrails/data-formatting/textFormatters';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import Link from '@alltrails/denali/components/Link';
import LoadingSpinner from '@alltrails/denali/components/LoadingSpinner';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Hide from '@alltrails/denali/icons/Hide';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import FeedbackConfirmationDialog from '@alltrails/layout/components/FeedbackConfirmationDialog';
import { Modal } from '@alltrails/modal';
import type { Review } from '@alltrails/types/Review';
import { useReviewSummaryContext } from '@/contexts/ReviewSummaryContext';
import { REVIEWS_SECTION_ID } from '@/utils/constants';
import cardStyles from '../../components/CardModule/CardModule.module.scss';
import ReviewSummaryAvatars from '../ReviewSummaryAvatars/ReviewSummaryAvatars';
import styles from './ReviewSummary.module.scss';
import useFeedbackProvided from './useFeedbackProvided';

type ReviewSummaryProps = {
  trailId: number;
  shuffledReviews?: Review[];
  isAdmin?: boolean;
  shouldExpand?: boolean;
  avgRating: number;
};

const ReviewSummary = ({ trailId, shuffledReviews, isAdmin, shouldExpand, avgRating }: ReviewSummaryProps) => {
  const user = useUser();
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const feedbackKey = useMemo(() => {
    if (user) {
      return `${user.id}-${trailId}`;
    }
    return `${trailId}`;
  }, [user, trailId]);

  const { isLoading: isReviewSummaryLoading, hideReviewSummary, reviewSummary, onToggleHideReviewSummary } = useReviewSummaryContext();

  const [showFullText, setShowFullText] = useState(false);
  const { isFeedbackProvided, provideFeedback } = useFeedbackProvided(feedbackKey, reviewSummary);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const toggleFullText = () => setShowFullText(!showFullText);
  const charLimit = 250;
  const showMoreButton = !!reviewSummary && !shouldExpand && reviewSummary?.length > charLimit;
  const reviewSummaryText = showMoreButton ? `${reviewSummary?.slice(0, charLimit)}...` : reviewSummary;

  const closeFeedbackModal = useCallback(() => setIsFeedbackModalOpen(false), []);

  const onClickYes = useCallback(() => {
    setIsFeedbackModalOpen(true);
    provideFeedback();
    logReviewGPTFeedbackClicked({ trail_id: trailId, feedback: 'helpful' });
  }, [provideFeedback, trailId]);

  const onClickNo = useCallback(() => {
    setIsFeedbackModalOpen(true);
    provideFeedback();
    logReviewGPTFeedbackClicked({ trail_id: trailId, feedback: 'not helpful' });
  }, [provideFeedback, trailId]);

  const onShowAllReviewsClick = () => {
    logTrailDetailsActionTapped({
      trail_id: String(trailId),
      trail_page_action: 'show all reviews',
      trail_page_location: 'chat gpt card'
    });
    const reviewSection = document.getElementById(REVIEWS_SECTION_ID);
    if (reviewSection) {
      reviewSection.focus();
      reviewSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const avatars = useMemo(() => {
    if (!shuffledReviews) {
      return [];
    }
    return shuffledReviews.map(review => ({
      userId: review.user.id,
      userName: fullName(review.user)
    }));
  }, [shuffledReviews]);

  const content = (summary: string, showMoreButton: boolean, showRating: boolean, showDisclaimer: boolean, inModal: boolean) => {
    return (
      <div className={classNames(styles.content, { [styles.modalContent]: inModal })}>
        {showRating ? (
          <div className={styles.avgRating}>
            <Typography variant="heading600">
              <FormattedNumber minimumFractionDigits={1} value={avgRating} />
            </Typography>
            <span role="img" aria-label={getScreenReaderRatingText(intl, avgRating)}>
              <StarFilled color="--color-text-primary" size="lg" />
            </span>
          </div>
        ) : (
          <ReviewSummaryAvatars avatars={avatars} />
        )}
        <div className={styles.summary}>
          <Typography variant="text200" color="primary">
            {summary}
          </Typography>
          {showMoreButton && !shouldExpand && (
            <Link testId="more-btn" onClick={toggleFullText} className={styles.link}>
              <FormattedMessage defaultMessage="more" />
            </Link>
          )}
        </div>
        {(showDisclaimer || !showMoreButton) && (
          <div className={styles.info}>
            <Typography variant="text100" color="secondary">
              <FormattedMessage defaultMessage="This summary is AI-generated from trail reviews and won’t always be perfect." />
            </Typography>
            {!isFeedbackProvided && (
              <div>
                <Typography variant="text100" color="secondary">
                  <FormattedMessage defaultMessage="Was it helpful?" />
                </Typography>
                <div className={styles.buttons}>
                  <Button
                    className={styles.button}
                    testId="review-summary-yes"
                    variant="flat"
                    text={<FormattedMessage defaultMessage="Yes" />}
                    onClick={onClickYes}
                  />
                  <div className={styles.divider} />
                  <Button
                    className={styles.button}
                    testId="review-summary-no"
                    variant="flat"
                    text={<FormattedMessage defaultMessage="No" />}
                    onClick={onClickNo}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {showRating && <ReviewSummaryAvatars avatars={avatars} />}
      </div>
    );
  };

  if (isAdmin && hideReviewSummary) {
    return (
      <div className={cardStyles.card}>
        <div className={cardStyles.inner}>
          {isReviewSummaryLoading ? (
            <div className={styles.adminContainer}>
              <LoadingSpinner />
            </div>
          ) : (
            <div className={styles.adminContainer}>
              <Hide size="lg" />
              <div className={styles.hiddenInfo}>
                <Typography variant="heading300" color="primary">
                  <FormattedMessage defaultMessage="Review summary hidden" />
                </Typography>
                <Typography variant="text200" color="primary">
                  <FormattedMessage defaultMessage="This summary was hidden by an admin and does not appear on the trail page." />
                </Typography>
              </div>
              <div className={styles.adminAction}>
                <Button text="Restore summary" onClick={onToggleHideReviewSummary} size="sm" variant="elevated" />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isAdmin && !hideReviewSummary && (!reviewSummary || !reviewSummaryText)) {
    return (
      <div className={cardStyles.card}>
        <div className={cardStyles.inner}>
          {isReviewSummaryLoading ? (
            <div className={styles.adminContainer}>
              <LoadingSpinner />
            </div>
          ) : (
            <div className={styles.adminContainer}>
              <div className={styles.hiddenInfo}>
                <Typography variant="heading300" color="primary">
                  <FormattedMessage defaultMessage="No review summary available" />
                </Typography>
              </div>
              <div className={styles.adminAction}>
                <Button
                  testId="review-summary-toggle-hide"
                  icon={{ Component: Hide }}
                  text={<FormattedMessage defaultMessage="Hide summary from trail page" />}
                  onClick={onToggleHideReviewSummary}
                  size="sm"
                  variant="elevated"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (hideReviewSummary || !reviewSummary || !reviewSummaryText) {
    return null;
  }

  return (
    <>
      <div className={cardStyles.card}>
        <div className={cardStyles.inner}>
          <Heading as="h2" className={cardStyles.cardTitle} size="300">
            <FormattedMessage defaultMessage="Trailgoers are saying" />
          </Heading>
          {content(reviewSummaryText, showMoreButton, !isMobile, isMobile, false)}
          {(isAdmin || !shouldExpand) && (
            <div className={styles.container}>
              <div className={styles.buttonContainer}>
                <div className={styles.action}>
                  {isAdmin ? (
                    <Button
                      testId="review-summary-toggle-hide"
                      icon={{ Component: Hide }}
                      text={<FormattedMessage defaultMessage="Hide summary from trail page" />}
                      onClick={onToggleHideReviewSummary}
                      size="md"
                      variant="elevated"
                    />
                  ) : (
                    <Button
                      title="Show all reviews"
                      testId="show-all-reviews-btn"
                      text={<FormattedMessage defaultMessage="Show all reviews" />}
                      size="md"
                      variant="elevated"
                      onClick={onShowAllReviewsClick}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <FeedbackConfirmationDialog durationMs={2000} isOpen={isFeedbackModalOpen} onRequestClose={closeFeedbackModal} />
      <Modal
        isOpen={showFullText}
        onCloseRequest={() => setShowFullText(false)}
        title={<FormattedMessage defaultMessage="Trailgoers are saying " />}
        modalClassName={styles.modal}
      >
        {content(reviewSummary, false, true, true, true)}
      </Modal>
    </>
  );
};

export default ReviewSummary;
